import React, { useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Modal,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { makeStyles } from 'tss-react/mui';
import { subDays, isBefore, isAfter } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const useStyles = makeStyles()(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'scroll',
  },
  container: {
    width: 340,
    padding: 40,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  title: {
    fontWeight: 900,
  },
  cancel: {
    borderRadius: 24,
    textTransform: 'unset',
    width: '100%',
  },
  btns: {
    alignSelf: 'flex-start',
    height: 56,
  },
}));

const RevertEventModal = ({ open, setOpen, revertEventFn, setIsPreview }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [selectedDate, setSelectedDate] = useState(null);
  const [dateError, setDateError] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  const minSelectableDate = subDays(new Date(), 30);
  const maxSelectableDate = new Date();

  const handleDateChange = date => {
    if (
      date &&
      !isBefore(date, minSelectableDate) &&
      !isAfter(date, maxSelectableDate)
    ) {
      setSelectedDate(date);
      setDateError(false);
    } else {
      setSelectedDate(null);
      setDateError(true);
    }
  };

  const handleConfirm = async () => {
    if (!selectedDate || dateError) return;
    setIsPreview(true);

    setIsReverting(true);

    // Format date as yyyy-MM-dd
    const formattedDate = format(selectedDate, 'yyyy-MM-dd');

    await revertEventFn(formattedDate);

    setIsReverting(false);
    setOpen(false);
  };

  const handleCancel = () => {
    setSelectedDate(null);
    setDateError(false);
    setOpen(false);
    setIsPreview(true);
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      slots={Backdrop}
      slotProps={{ timeout: 500 }}
      onClose={handleCancel}
      closeAfterTransition
      data-hook='revert-event-modal'
    >
      <Fade in={open}>
        <Paper className={classes.container}>
          <Typography variant='h6' className={classes.title}>
            {t('vehicleHistoryModal.dialogMessage')}
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t('vehicleHistoryModal.revertDate')}
              value={selectedDate}
              onChange={handleDateChange}
              minDate={minSelectableDate}
              maxDate={maxSelectableDate}
              shouldDisableDate={date =>
                isBefore(date, minSelectableDate) ||
                isAfter(date, maxSelectableDate)
              }
              format='yyyy-MM-dd'
              sx={{ width: '100%' }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: dateError,
                  helperText: dateError
                    ? t('errors.invalidDate') ||
                      'Please select a valid date (last 30 days)'
                    : '',
                },
              }}
            />
          </LocalizationProvider>

          <Box display='flex' gap='10px' justifyContent='center'>
            <Button
              variant='outlined'
              size='small'
              onClick={handleCancel}
              data-hook='cancel_revert'
            >
              {t('common.cancel')}
            </Button>
            <Button
              color='primary'
              variant='contained'
              onClick={handleConfirm}
              disabled={!selectedDate || dateError || isReverting}
              data-hook='confirm_revert'
            >
              {isReverting ? (
                <CircularProgress size={24} />
              ) : (
                t('common.confirm')
              )}
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default RevertEventModal;
